import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary-50/30 to-orange-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="border-b border-slate-200/50 dark:border-slate-700/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate(-1)} variant="ghost" size="sm" className="gap-2 dark:text-slate-200">
              <ArrowLeft className="w-4 h-4" />
              Powrót
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-2">
              <img src="/kai-logo.png" alt="KAI" className="w-12 h-12 object-contain rounded-lg" />
              <span className="font-heading text-primary-900 dark:text-slate-100 font-bold">KAI</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="font-heading text-3xl md:text-4xl text-primary-900 dark:text-slate-100 mb-2">Regulamin Serwisu KAI</h1>
          <p className="text-muted-foreground mb-8">Ostatnia aktualizacja: 7 lutego 2026 r.</p>
          
          <div className="prose prose-slate max-w-none space-y-8 text-sm leading-relaxed">
            
            {/* § 1 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 1. Definicje</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Serwis</strong> – aplikacja internetowa KAI (Kurdynowski AI) dostępna pod adresem internetowym wskazanym przez Usługodawcę.</li>
                <li><strong>Usługodawca / Sprzedawca</strong> – Biuro Rachunkowe Kurdynowski, ul. Kościuszki 27, 85-079 Bydgoszcz, NIP: 5541008895, właściciel i operator Serwisu KAI.</li>
                <li><strong>Użytkownik</strong> – osoba fizyczna, prawna lub jednostka organizacyjna nieposiadająca osobowości prawnej, korzystająca z Serwisu.</li>
                <li><strong>Konto</strong> – indywidualne konto Użytkownika w Serwisie, chronione loginem i hasłem.</li>
                <li><strong>Subskrypcja</strong> – odpłatna usługa umożliwiająca pełny dostęp do funkcjonalności Serwisu.</li>
                <li><strong>KAI</strong> – asystent oparty na sztucznej inteligencji, stanowiący główną funkcjonalność Serwisu.</li>
              </ol>
            </section>

            {/* § 2 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 2. Postanowienia Ogólne</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Niniejszy Regulamin określa zasady korzystania z Serwisu KAI oraz prawa i obowiązki Użytkowników.</li>
                <li>Właścicielem i operatorem Serwisu jest <strong>Biuro Rachunkowe Kurdynowski</strong>, ul. Kościuszki 27, 85-079 Bydgoszcz, NIP: 5541008895.</li>
                <li>Serwis świadczy usługi wsparcia dla księgowych i specjalistów ds. rachunkowości w zakresie polskiego prawa księgowego, podatkowego i rachunkowego przy wykorzystaniu technologii sztucznej inteligencji.</li>
                <li>Korzystanie z Serwisu oznacza akceptację niniejszego Regulaminu oraz Polityki Prywatności.</li>
                <li>Użytkownik zobowiązany jest do zapoznania się z treścią Regulaminu przed rozpoczęciem korzystania z Serwisu.</li>
              </ol>
            </section>

            {/* § 3 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 3. Warunki Techniczne</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Do korzystania z Serwisu niezbędne jest:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>urządzenie z dostępem do sieci Internet,</li>
                    <li>aktualna przeglądarka internetowa (Chrome, Firefox, Safari, Edge),</li>
                    <li>aktywny adres e-mail.</li>
                  </ul>
                </li>
                <li>Usługodawca nie ponosi odpowiedzialności za problemy techniczne wynikające z niespełnienia powyższych wymagań.</li>
                <li>Usługodawca zastrzega sobie prawo do przerw technicznych w działaniu Serwisu, o czym Użytkownicy zostaną powiadomieni z odpowiednim wyprzedzeniem.</li>
              </ol>
            </section>

            {/* § 4 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 4. Rejestracja i Konto Użytkownika</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Korzystanie z pełnej funkcjonalności Serwisu wymaga utworzenia Konta.</li>
                <li>Rejestracja wymaga podania prawdziwych danych osobowych, w tym adresu e-mail.</li>
                <li>Użytkownik zobowiązuje się do:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>zachowania poufności danych dostępowych do Konta,</li>
                    <li>nieudostępniania Konta osobom trzecim,</li>
                    <li>niezwłocznego informowania Usługodawcy o nieautoryzowanym dostępie do Konta.</li>
                  </ul>
                </li>
                <li>Usługodawca zastrzega sobie prawo do zablokowania lub usunięcia Konta w przypadku naruszenia Regulaminu.</li>
              </ol>
            </section>

            {/* § 5 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 5. Subskrypcja i Płatności</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Dostęp do pełnej funkcjonalności Serwisu wymaga wykupienia Subskrypcji.</li>
                <li>Dostępne plany Subskrypcji:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Plan Miesięczny:</strong> 59,00 zł netto (72,57 zł brutto z VAT 23%) – okres rozliczeniowy: 30 dni.</li>
                    <li><strong>Plan Roczny:</strong> 601,80 zł netto (740,21 zł brutto z VAT 23%) – okres rozliczeniowy: 365 dni. Rabat 15% w stosunku do płatności miesięcznych.</li>
                  </ul>
                </li>
                <li>Nowi Użytkownicy otrzymują 7-dniowy bezpłatny okres próbny.</li>
                <li>Płatności obsługiwane są przez operatorów:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Tpay</strong> – BLIK, przelewy bankowe, karty płatnicze,</li>
                    <li><strong>Stripe</strong> – karty płatnicze międzynarodowe.</li>
                  </ul>
                </li>
                <li>Subskrypcja odnawia się automatycznie, chyba że Użytkownik ją anuluje przed końcem okresu rozliczeniowego.</li>
                <li>Faktury VAT są generowane automatycznie i dostępne do pobrania w panelu Użytkownika.</li>
              </ol>
            </section>

            {/* § 6 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 6. Zakres Usług i Ograniczenia</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Serwis KAI zapewnia:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>dostęp do asystenta AI specjalizującego się w polskim prawie księgowym i rachunkowym,</li>
                    <li>możliwość zadawania pytań i otrzymywania odpowiedzi z cytowaniem podstaw prawnych,</li>
                    <li>historię konwersacji,</li>
                    <li>wybór modelu AI (OpenAI, Claude, Gemini).</li>
                  </ul>
                </li>
                <li><strong>Informacje udzielane przez KAI mają charakter wyłącznie informacyjny i pomocniczy.</strong></li>
                <li><strong>KAI nie zastępuje profesjonalnej porady prawnej, podatkowej ani księgowej.</strong></li>
                <li>Użytkownik powinien weryfikować uzyskane informacje u wykwalifikowanych specjalistów przed podjęciem decyzji biznesowych lub prawnych.</li>
              </ol>
            </section>

            {/* § 7 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 7. Odpowiedzialność</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Usługodawca dokłada wszelkich starań, aby informacje udzielane przez KAI były aktualne, rzetelne i zgodne z obowiązującym prawem.</li>
                <li><strong>Usługodawca nie ponosi odpowiedzialności za:</strong>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>decyzje podjęte przez Użytkownika na podstawie informacji uzyskanych z Serwisu,</li>
                    <li>szkody wynikłe z błędnej interpretacji lub zastosowania uzyskanych informacji,</li>
                    <li>przerwy w działaniu Serwisu spowodowane czynnikami zewnętrznymi,</li>
                    <li>utratę danych wynikającą z działania osób trzecich lub siły wyższej.</li>
                  </ul>
                </li>
                <li>Użytkownik ponosi pełną odpowiedzialność za sposób wykorzystania informacji uzyskanych z Serwisu.</li>
              </ol>
            </section>

            {/* § 8 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 8. Prawa Autorskie i Własność Intelektualna</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Wszelkie prawa autorskie do Serwisu KAI, w tym kod źródłowy, interfejs graficzny, logo, nazwa i treści, przysługują <strong>Biuro Rachunkowe Kurdynowski</strong>, ul. Kościuszki 27, 85-079 Bydgoszcz, NIP: 5541008895.</li>
                <li>Użytkownik otrzymuje ograniczoną, niewyłączną, nieprzenoszalną licencję na korzystanie z Serwisu wyłącznie na własny użytek.</li>
                <li>Zabronione jest kopiowanie, modyfikowanie, rozpowszechnianie lub wykorzystywanie komercyjne jakichkolwiek elementów Serwisu bez pisemnej zgody właściciela.</li>
                <li>Szczegółowe informacje o prawach autorskich zawarte są w dokumencie "Prawa Własności Intelektualnej".</li>
              </ol>
            </section>

            {/* § 9 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 9. Prawo Odstąpienia od Umowy</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Użytkownik będący konsumentem ma prawo odstąpić od umowy w terminie 14 dni od dnia jej zawarcia, bez podawania przyczyny.</li>
                <li>Aby skorzystać z prawa odstąpienia, Użytkownik powinien poinformować Usługodawcę o swojej decyzji drogą mailową.</li>
                <li>W przypadku skutecznego odstąpienia, Usługodawca zwróci wszystkie otrzymane płatności w terminie 14 dni.</li>
                <li>Prawo odstąpienia nie przysługuje, jeżeli Użytkownik wyraził zgodę na rozpoczęcie świadczenia usługi przed upływem terminu do odstąpienia i został poinformowany o utracie tego prawa.</li>
              </ol>
            </section>

            {/* § 10 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 10. Reklamacje</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Użytkownik ma prawo złożyć reklamację dotyczącą działania Serwisu.</li>
                <li>Reklamacje należy składać drogą elektroniczną na adres: <strong>crm@kurdynowski.pl</strong></li>
                <li>Reklamacja powinna zawierać: dane Użytkownika, opis problemu oraz oczekiwany sposób rozwiązania.</li>
                <li>Usługodawca rozpatrzy reklamację w terminie 14 dni od jej otrzymania.</li>
              </ol>
            </section>

            {/* § 11 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 11. Rozwiązanie Umowy</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Użytkownik może rozwiązać umowę w każdym czasie poprzez anulowanie Subskrypcji i usunięcie Konta.</li>
                <li>Usługodawca może rozwiązać umowę ze skutkiem natychmiastowym w przypadku:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>rażącego naruszenia Regulaminu przez Użytkownika,</li>
                    <li>działań na szkodę Serwisu lub innych Użytkowników,</li>
                    <li>podania nieprawdziwych danych podczas rejestracji.</li>
                  </ul>
                </li>
                <li>Rozwiązanie umowy nie zwalnia Użytkownika z obowiązku uregulowania zaległych płatności.</li>
              </ol>
            </section>

            {/* § 12 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 12. Zmiany Regulaminu</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Usługodawca zastrzega sobie prawo do zmiany Regulaminu.</li>
                <li>O zmianach Użytkownicy zostaną powiadomieni z co najmniej 14-dniowym wyprzedzeniem drogą mailową lub poprzez komunikat w Serwisie.</li>
                <li>Dalsze korzystanie z Serwisu po wejściu zmian w życie oznacza akceptację nowego Regulaminu.</li>
              </ol>
            </section>

            {/* § 13 */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">§ 13. Postanowienia Końcowe</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>W sprawach nieuregulowanych niniejszym Regulaminem zastosowanie mają przepisy prawa polskiego, w szczególności Kodeksu cywilnego oraz ustawy o świadczeniu usług drogą elektroniczną.</li>
                <li>Wszelkie spory wynikające z niniejszego Regulaminu będą rozstrzygane przez sądy powszechne właściwe dla siedziby Usługodawcy.</li>
                <li>Jeżeli którekolwiek z postanowień Regulaminu zostanie uznane za nieważne, pozostałe postanowienia pozostają w mocy.</li>
                <li>Regulamin wchodzi w życie z dniem 7 lutego 2026 r.</li>
              </ol>
            </section>

            <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
              <h3 className="font-heading text-lg text-primary-900 dark:text-slate-100 mb-2">Dane Usługodawcy</h3>
              <p className="text-slate-700">
                <strong>Usługodawca / Sprzedawca:</strong> Biuro Rachunkowe Kurdynowski<br />
                <strong>Adres:</strong> ul. Kościuszki 27, 85-079 Bydgoszcz<br />
                <strong>NIP:</strong> 5541008895<br />
                <strong>E-mail:</strong> crm@kurdynowski.pl<br />
                <strong>Serwis:</strong> KAI (Kurdynowski AI)
              </p>
            </div>

            <p className="text-muted-foreground text-center mt-8">
              © 2026 KAI - Kurdynowski AI. Wszelkie prawa zastrzeżone.<br />
              Biuro Rachunkowe Kurdynowski, ul. Kościuszki 27, 85-079 Bydgoszcz, NIP: 5541008895
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
